import React, { useEffect } from 'react';

import { SUBSCRIPTION_POP_UP_CLOSE } from '@editor/global/analytics/events';
import analytics from '@repo/analytics';
import { isMobileOperatingSystem as getIsMobileOperatingSystem } from '@repo/detection';
import { TeamRole } from '@repo/domain/team';
import { withEventScope } from '@repo/event-processor';
import { fireToast } from '@repo/ui/components/Toast';

import teamsApi from '../../api/teams';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../stores/planSelectionStore';
import {
  promotionSelector,
  usePromotionStore,
} from '../../stores/promotionStore';
import { useUserStore, userStoreSelector } from '../../stores/userStore';
import DecisionModal from '../DecisionModal/DecisionModal';
import { SubscriptionSuccessModal } from '../Subscription';

import ModalContainer from './ModalContainer';
import { RevampedCheckoutFlowMobile } from './RevampedCheckoutFlowMobile';
import UpgradeFlowModalContent from './UpgradeFlowModalContent';

/**
 * This component mounts the upgrade flow modal.
 */
const UpgradeFlowModal: React.FC = () => {
  const user = useUserStore(userStoreSelector.user);

  const initPromotionStore = usePromotionStore(promotionSelector.init);
  const {
    isOpen,
    upgradeSuccessModalIsOpen,
    returnUrlFailed,
    isOnlyExpertUpgrade,
    resetReturnUrlFailed,
    onClose,
    onSuccess,
    contentType,
    ...contentProps
  } = usePromotionStore(promotionSelector.upgradeModalProps);

  const { reset } = usePlanSelectionStore(planSelectionStoreSelector.actions);
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);

  const shouldShowRevampedMobileCheckoutFlow = getIsMobileOperatingSystem();

  useEffect(() => {
    if (!user) {
      onClose();
      return;
    }

    void initPromotionStore();
    // onCloseSubscriptionModal results on "Maximum update depth exceeded" error
    // if added to the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPromotionStore, user]);

  if (!contentType) {
    return (
      <SubscriptionSuccessModal
        isOpen={upgradeSuccessModalIsOpen}
        onClose={onSuccess}
      />
    );
  }

  const handleClose = (): void => {
    onClose();
    setTimeout(() => {
      reset();
    }, 300);

    analytics.track(SUBSCRIPTION_POP_UP_CLOSE, {
      selectedPlan: plan,
      duration: yearly ? 'yearly' : 'monthly',
    });
  };

  if (user?.team && user.team.role !== TeamRole.Admin) {
    const handleRequestUpgrade = async (): Promise<void> => {
      const response = await teamsApi.requestUpgrade();
      if (!response.success) {
        fireToast({
          label:
            'Failed to send a request to upgrade your plan. Please try again.',
          error: true,
          duration: 5000,
        });
      } else {
        fireToast({
          label: 'Sent a request to upgrade your plan to your team admin.',
          duration: 5000,
        });
        handleClose();
      }
    };

    return (
      <DecisionModal
        firstButton={{
          label: 'Request Upgrade',
          type: 'primary',
          onClick: handleRequestUpgrade,
          width: 'auto',
        }}
        isOpen={isOpen}
        message={`Your team is using a ${
          user.team.plan ?? 'Free'
        } plan. Only your team admin can upgrade your account.`}
        title="Upgrade Plan"
        onClose={handleClose}
      />
    );
  }

  const UpgradeFlowModalContentContainer = shouldShowRevampedMobileCheckoutFlow
    ? RevampedCheckoutFlowMobile
    : ModalContainer;

  return (
    <UpgradeFlowModalContentContainer isOpen={isOpen} onClose={handleClose}>
      <UpgradeFlowModalContent
        contentType={contentType}
        isOnlyExpertUpgrade={isOnlyExpertUpgrade}
        resetReturnUrlFailed={resetReturnUrlFailed}
        returnUrlFailed={returnUrlFailed}
        onClose={handleClose}
        onSuccess={onSuccess}
        {...contentProps}
      />
    </UpgradeFlowModalContentContainer>
  );
};

export default withEventScope(UpgradeFlowModal, {
  feature: 'upgradeFlowModal',
});
